import { Group, Space, Text } from '@mantine/core';
import { AnchorButton } from '@root/Design/Primitives';
import { useDi } from '@root/Services/DI';
import { ResourceService } from '@root/Services/Resources/ResourceService';
import { Fragment, useEffect, useState } from 'react';
import { ResourceTagService } from '../Services/ResourceTagService';
import { BulkTagProps } from './BulkTagProps';
import { TagEditor } from './TagEditor';
import { TagField } from './TagField';
import { CreateLabelKey, validateLabelValue, LABEL_PREFIX } from '@root/Components/Resources/Gcp/LabelUtils';

export const ChangeLabelValue = (props: BulkTagProps) => {
    const [labelKeyList, setLabelKeyList] = useState<string[]>([]);
    const [currentValuesPicklist, setCurrentValuesPicklist] = useState<string[]>([]);
    const [newValuesPicklist, setNewValuesPicklist] = useState<string[]>([]);
    const resourceSvc = useDi(ResourceService);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
    const [keyToUpdate, setKeyToUpdate] = useState<string>('');
    const [valuesToReplace, setValuesToReplace] = useState<string[]>(['']);
    const [newValue, setNewValue] = useState<string>('');
    const [canAdd, setCanAdd] = useState<boolean>(false);
    const [canRemove, setCanRemove] = useState<boolean>(false);
    const [selectedKeyError, setSelectedKeyError] = useState<string>('');
    const [selectedValueErrors, setSelectedValueErrors] = useState<Map<number, string>>(new Map());
    const [newValueError, setNewValueError] = useState<string | null>(null);
    const tagSvc = useDi(ResourceTagService);

    useEffect(() => {
        if (props.tagstoUpdate && props.updateToValue && props.tagKeyGettingValue) {
            setKeyToUpdate(props.tagKeyGettingValue);
            setCurrentValuesPicklist(props.tagstoUpdate);
        }
    }, []);

    useEffect(() => {
        (async () => {
            const keys = await getKeysOfSelected();
            setLabelKeyList(keys);
            setNewValuesPicklist((await resourceSvc.getAllTagValues()).sort());
            setCurrentValuesPicklist(await getValuesOfSelected());
        })();
    }, [props.resourceIds.count()]);

    useEffect(() => {
        (async () => {
            setCurrentValuesPicklist(await getValuesOfSelected());
        })();
        if (props.tagstoUpdate && props.updateToValue && props.tagKeyGettingValue) {
            setValuesToReplace(props.tagstoUpdate);
            setNewValue(props.updateToValue);
        } else {
            setValuesToReplace(['']);
            setNewValue('');
        }
    }, [keyToUpdate]);

    useEffect(() => {
        if (valuesToReplace.length > 1) {
            setCanRemove(true);
        } else {
            setCanRemove(false);
        }
        setCanAdd(currentValuesPicklist.filter((i) => !valuesToReplace.includes(i)).length > 0 && valuesToReplaceIsValid());
        formIsValid();

        if (newValue) {
            const error = validateLabelValue(newValue);
            setNewValueError(error);
        } else {
            setNewValueError(null);
        }
    }, [newValue, JSON.stringify(valuesToReplace), currentValuesPicklist.length]);

    const getKeysOfSelected = async () => {
        if (props.tagKeyGettingValue) {
            return [props.tagKeyGettingValue];
        } else {
            const keys = await tagSvc.getValidKeys(props.resourceIds);
            return keys.filter((key) => key.startsWith(LABEL_PREFIX)).map((key) => key.substring(6));
        }
    };

    const getValuesOfSelected = async () => {
        if (props.tagstoUpdate) {
            return props.tagstoUpdate;
        } else {
            const fullKey = CreateLabelKey(keyToUpdate);
            return await tagSvc.getValidValues(props.resourceIds, fullKey);
        }
    };

    const valuesToReplaceIsValid = () => {
        return !valuesToReplace.includes('');
    };

    const formIsValid = async () => {
        let isValid =
            valuesToReplaceIsValid() &&
            keyToUpdate !== '' &&
            valuesToReplaceIsValid() &&
            newValue !== '' &&
            validateLabelValue(newValue ?? '') === null;
        setDisableSubmit(!isValid);

        return isValid;
    };

    const addRowHandler = () => {
        setValuesToReplace((prevState) => {
            let newState = [...prevState];
            newState.push('');
            return newState;
        });
    };

    const removeRowHandler = (index: number) => {
        setValuesToReplace((prevState) => {
            let newState = [...prevState];
            newState.splice(index, 1);
            return newState;
        });
    };

    const setKeyValue = (_: number, value: string) => {
        setKeyToUpdate(value);
    };

    const addValueToReplace = (index: number, value: string) => {
        setValuesToReplace((prevState) => {
            let newState = [...prevState];
            newState.splice(index, 1, value);
            return newState;
        });
    };

    const setReplacementValue = (_: number, value: string) => {
        setNewValue(value);
    };

    const tagJob = tagSvc.createTagJob(props.resourceIds, {
        ReplaceValues: {
            Key: CreateLabelKey(keyToUpdate),
            Replacement: newValue,
            Values: valuesToReplace,
        },
    });

    return (
        <TagEditor
            onButtonClick={props.onButtonClick}
            companyId={props.companyId}
            resourceIds={props.resourceIds}
            pageTitle={props.pageTitle}
            instructions={props.instructions}
            canSubmit={!disableSubmit}
            setDisableSubmit={setDisableSubmit}
            onTagJobStarting={props.onTagJobStarting}
            onTagJobStarted={props.onTagJobStarted}
            operationType="renameValue"
            tagJob={tagJob}
            overwriteConflicts={false}
            setOverwriteConflicts={() => null}
        >
            <>
                <TagField
                    atid={'LabelKeyInput'}
                    key={`changeLabelValue0`}
                    fieldValue={keyToUpdate}
                    valuePickList={labelKeyList}
                    label="Select Label"
                    setSelectedValue={setKeyValue}
                    canAdd={false}
                    canRemove={false}
                    receiveInitalFocus={true}
                    type="key"
                />
                <Text color="error" hidden={selectedKeyError == undefined || selectedKeyError.length === 0}>
                    *{selectedKeyError}
                </Text>
                <Space h="sm" />
                {valuesToReplace.map((_, index) => (
                    <Fragment key={`fragment${index}`}>
                        <Group noWrap grow key={`groupLabel${index}`} spacing={0}>
                            <TagField
                                atid={'LabelOldValueInput:' + index}
                                key={`changeLabelValue${index + 2}`}
                                fieldValue={valuesToReplace[index]}
                                index={index}
                                count={valuesToReplace.length}
                                valuePickList={currentValuesPicklist.filter((i) => !valuesToReplace.includes(i))}
                                label="Value to replace"
                                setSelectedValue={addValueToReplace}
                                handleAddRow={addRowHandler}
                                handleRemoveRow={removeRowHandler}
                                canAdd={canAdd}
                                canRemove={canRemove}
                                disabled={selectedKeyError.length > 0}
                            />
                        </Group>
                        <Text key={`labelError${index}`} color="error" hidden={!selectedValueErrors.has(index)}>
                            *{selectedValueErrors.get(index)}
                        </Text>
                        <Space key={`spaceLabel${index}`} h="sm" />
                    </Fragment>
                ))}
                <AnchorButton text="Add" onClick={addRowHandler} icon={<i className="ti ti-circle-plus"></i>} atid="RenameLabelValuesAddAnchor" />
                <Space h="sm" />
                <TagField
                    key={`changeLabelValue1`}
                    atid={'LabelNewValueInput'}
                    fieldValue={newValue}
                    valuePickList={newValuesPicklist}
                    label="Replacement value"
                    setSelectedValue={setReplacementValue}
                    canAdd={false}
                    canRemove={false}
                    disabled={selectedKeyError.length > 0}
                    error={newValue ? newValueError : undefined}
                />
            </>
        </TagEditor>
    );
};
